import {
  useAskHelenaConversations,
  useDeleteAskHelenaConversation,
  usePinAskHelenaConversation,
  useRenameAskHelenaConversation,
  useUnpinAskHelenaConversation,
} from "controller";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { getApiErrorMessage } from "utilz/helpers";
import { recordAction, trackAiHome } from "../../analytics";
import {
  askHelenaConversationId,
  askHelenaMessages,
  pendingDeleteConversationIdAtom,
  renamingConversationIdAtom,
  selectedConversationIdAtom,
  startNewChat,
} from "../store";

/**
 * A 404 is the service's answer to every kind of "no" on these routes —
 * already deleted, never existed, or someone else's thread. The three are
 * deliberately indistinguishable, so the UI must not invent a distinction.
 */
const GONE_STATUS = 404;
const GONE_MESSAGE = "That chat is no longer available.";

const isGone = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  "status" in error &&
  (error as { status: unknown }).status === GONE_STATUS;

const reportFailure = (error: unknown, fallback: string) => {
  toast.error(
    isGone(error) ? GONE_MESSAGE : getApiErrorMessage(error, fallback),
  );
};

/**
 * Pin, rename and delete for a conversation row, in one place so the sidebar
 * and the panel header behave identically and stay presentational.
 *
 * The mutations themselves live in `controller/` — this hook owns only the
 * feature-side consequences: which row is editing, whether the delete
 * confirmation is open, and what happens when the thread being deleted is the
 * one on screen.
 */
export const useChatConversationActions = () => {
  const liveConversationId = useAtomValue(askHelenaConversationId);
  const selectedConversationId = useAtomValue(selectedConversationIdAtom);
  const [renamingId, setRenamingId] = useAtom(renamingConversationIdAtom);
  const [pendingDeleteId, setPendingDeleteId] = useAtom(
    pendingDeleteConversationIdAtom,
  );
  const newChat = useSetAtom(startNewChat);

  const { data } = useAskHelenaConversations();
  const { mutateAsync: pinConversation } = usePinAskHelenaConversation();
  const { mutateAsync: unpinConversation } = useUnpinAskHelenaConversation();
  const { mutateAsync: renameConversation } = useRenameAskHelenaConversation();
  const { mutateAsync: deleteConversation, isLoading: isDeleting } =
    useDeleteAskHelenaConversation();

  const readOpenThread = useAtomCallback(
    useCallback(
      (get) => ({
        conversationId: get(askHelenaConversationId),
        queryCount: get(askHelenaMessages).filter(
          (message) => message.role === "user",
        ).length,
      }),
      [],
    ),
  );

  const trackPinAction = useCallback(
    (conversationId: string, isPinned: boolean) => {
      const title =
        data?.items.find((conversation) => conversation.id === conversationId)
          ?.title ?? null;
      const openThread = readOpenThread();

      recordAction(isPinned ? "pin_chat" : "unpin_chat");

      if (!isPinned) {
        trackAiHome("ai_chat_unpinned", {
          chat_id: conversationId,
          chat_title: title,
        });
        return;
      }

      trackAiHome("ai_chat_pinned", {
        chat_id: conversationId,
        chat_title: title,
        query_count_in_chat:
          openThread.conversationId === conversationId
            ? openThread.queryCount
            : null,
      });
    },
    [data?.items, readOpenThread],
  );

  const pin = useCallback(
    async (conversationId: string) => {
      try {
        await pinConversation({ conversationId });
        trackPinAction(conversationId, true);
      } catch (error: unknown) {
        // The 21st pin is a 400 whose message is written to be read by a user
        // ("You can pin up to 20 conversations. Unpin one first."), so it is
        // shown verbatim rather than replaced with copy of our own.
        reportFailure(error, "Couldn't pin this chat.");
      }
    },
    [pinConversation, trackPinAction],
  );

  const unpin = useCallback(
    async (conversationId: string) => {
      try {
        await unpinConversation({ conversationId });
        trackPinAction(conversationId, false);
      } catch (error: unknown) {
        reportFailure(error, "Couldn't unpin this chat.");
      }
    },
    [trackPinAction, unpinConversation],
  );

  const rename = useCallback(
    async (conversationId: string, title: string) => {
      // Close the editor first: the cache patch has already landed, so leaving
      // it open would show an input over the new label.
      setRenamingId(null);
      try {
        await renameConversation({ conversationId, title });
      } catch (error: unknown) {
        reportFailure(error, "Couldn't rename this chat.");
      }
    },
    [renameConversation, setRenamingId],
  );

  const confirmDelete = useCallback(async () => {
    if (!pendingDeleteId) return;

    try {
      await deleteConversation({ conversationId: pendingDeleteId });
      // Deleting the thread on screen leaves the panel rendering something the
      // service will now 404 on, so send the user back to the empty state. The
      // URL sync hook drops `?conversation=` on its own from there.
      if (
        pendingDeleteId === liveConversationId ||
        pendingDeleteId === selectedConversationId
      ) {
        newChat();
      }
      setPendingDeleteId(null);
    } catch (error: unknown) {
      reportFailure(error, "Couldn't delete this chat.");
      setPendingDeleteId(null);
    }
  }, [
    deleteConversation,
    liveConversationId,
    newChat,
    pendingDeleteId,
    selectedConversationId,
    setPendingDeleteId,
  ]);

  /**
   * Pin state of one thread, read from the list already in cache rather than
   * fetched again. A thread that is not on the cached page reads as unpinned;
   * both calls are idempotent, so a wrong guess costs a no-op round trip.
   */
  const isPinned = useCallback(
    (conversationId: string): boolean =>
      data?.items.some(
        (conversation) =>
          conversation.id === conversationId && conversation.pinnedAt !== null,
      ) ?? false,
    [data?.items],
  );

  return {
    renamingId,
    startRename: setRenamingId,
    cancelRename: useCallback(() => setRenamingId(null), [setRenamingId]),
    rename,
    pin,
    unpin,
    isPinned,
    pendingDeleteId,
    requestDelete: setPendingDeleteId,
    cancelDelete: useCallback(
      () => setPendingDeleteId(null),
      [setPendingDeleteId],
    ),
    confirmDelete,
    isDeleting,
  };
};
